/**
 * TaskFormModal's state shape and its conversions, in one place.
 *
 * Moved out of TaskFormModal.jsx verbatim (RESP1, Regel 48): the modal
 * had grown past 700 lines and this layer is the part of it that has
 * nothing to do with React — an empty form, a row read into one, and a
 * form written back onto the wire. Keeping it here means the modal reads
 * as what it is, an orchestrator, and means these conversions can be
 * exercised without mounting a dialog.
 *
 * Pure move. No renames, no reordering, no "while we're here" fixes —
 * the comments below are the originals and still describe the same
 * decisions they described in the modal.
 */
import {
  ENTITY_IDS_FIELDS,
  manualIdsByField,
} from "../entityLinks/multiEntityLinks";
import {
  allDayInputToIso,
  businessLocalToIso,
  isoToBusinessLocalInput,
} from "../../../utils/datetime";

// ──────────────────────────────────────────────────────────────────────
// Modal-only datetime helpers
// ──────────────────────────────────────────────────────────────────────

/** Format an ISO datetime for a native `<input type="datetime-local">`.
 *
 * Swiss wall clock, not the device's: a task due at 18:00 has to read
 * 18:00 in the form whether it is opened in Zurich or in Istanbul. */
export function isoToDatetimeLocalValue(iso) {
  if (!iso) return "";
  try {
    return isoToBusinessLocalInput(iso);
  } catch {
    return "";
  }
}

/** Convert a `<input type="datetime-local">` value back to ISO; "" → null.
 *
 * The typed value is read as a SWISS wall clock. It used to go through
 * `new Date(v)`, which reads an offset-less string in the device's zone
 * — so the same "18:00" became a different instant for every country
 * the form was filled in from. */
export function datetimeLocalToIso(v) {
  if (!v) return null;
  return businessLocalToIso(v);
}


// ──────────────────────────────────────────────────────────────────────
// Form state shape + conversions
// ──────────────────────────────────────────────────────────────────────

export const EMPTY_FORM = {
  title: "",
  description: "",
  task_type: "general",
  // CAT1: the area of work. "" means "not chosen"; the form requires a
  // choice for NEW tasks while the API keeps accepting null, so existing
  // rows stay openable and older clients keep working.
  activity_category: "",
  priority: "medium",
  due_at: "",
  all_day: false,
  start_at: "",
  end_at: "",
  assigned_to_user_id: "",
  // ASSIGN1: the people helping, beside the one responsible above.
  participant_user_ids: [],
  // ME3a: the seven entity links are MANUAL id SETS now. The singular
  // ``*_id`` fields are gone from the form entirely — sending both a
  // singular and a plural value for one type is a 422 on the backend,
  // and the plural set is the complete answer.
  tenant_ids: [],
  tenancy_ids: [],
  room_ids: [],
  unit_ids: [],
  property_ids: [],
  application_ids: [],
  landlord_ids: [],
};

export function taskToForm(task) {
  if (!task) return { ...EMPTY_FORM };
  return {
    title: task.title || "",
    description: task.description || "",
    task_type: task.task_type || "general",
    activity_category: task.activity_category || "",
    priority: task.priority || "medium",
    due_at: isoToDatetimeLocalValue(task.due_at),
    all_day: !!task.all_day,
    start_at: isoToDatetimeLocalValue(task.start_at),
    end_at: isoToDatetimeLocalValue(task.end_at),
    assigned_to_user_id: task.assigned_to_user_id || "",
    // ASSIGN1: from the DETAIL response, which is the only shape that
    // carries them with labels (PICK1a+b — a list row is not an edit
    // source). Never includes the assignee: the server keeps the two
    // roles disjoint, so nothing has to be subtracted here.
    participant_user_ids: Array.isArray(task.participant_user_ids)
      ? task.participant_user_ids.map(String)
      : [],
    // ME3a: only the MANUAL links become form state. A derived link is a
    // consequence of a manual one; loading it as a selection would make
    // the next save persist it as a deliberate choice — and it would
    // then outlive the selection it came from. Falls back to the legacy
    // singular field when the response predates ME2a.
    ...manualIdsByField(task),
  };
}

export function formToPayload(form) {
  const startIso = datetimeLocalToIso(form.start_at);
  const endIso = datetimeLocalToIso(form.end_at);
  return {
    title: form.title.trim(),
    description: form.description.trim() ? form.description.trim() : null,
    task_type: form.task_type,
    // Empty select → null on the wire, which the backend reads as
    // "not classified". Never the empty string.
    activity_category: form.activity_category || null,
    priority: form.priority,
    // An all-day task is a DATE, so only the date half of the picker is
    // sent, anchored at UTC midnight. Running it through the wall-clock
    // conversion like a timed value would place it a couple of hours
    // after Swiss midnight, and everything that reads the day back by
    // slicing the wire string would then name the day before.
    due_at: form.all_day
      ? allDayInputToIso(form.due_at)
      : datetimeLocalToIso(form.due_at),
    all_day: !!form.all_day,
    start_at: form.all_day ? null : startIso,
    end_at: form.all_day ? null : endIso,
    assigned_to_user_id: form.assigned_to_user_id || null,
    // ASSIGN1: always sent, like every other field this form owns. ``[]``
    // therefore means "remove all participants" — which is exactly what
    // an emptied picker means. It is never ``null`` (a 422 by contract),
    // and never contains the assignee, because the field below excludes
    // them from its options and drops them on promotion.
    participant_user_ids: Array.isArray(form.participant_user_ids)
      ? form.participant_user_ids
      : [],
    // ME3a: the seven plural sets, and NO singular ``*_id`` keys. The
    // backend treats a transmitted plural set as the authoritative full
    // manual set for that type, so ``[]`` clears it — which is exactly
    // what an emptied picker means. It never receives ``null`` for one
    // of these (that is a 422 by contract), and it is never sent a
    // derived id, because the form only ever held manual ones.
    ...ENTITY_IDS_FIELDS.reduce((acc, field) => {
      acc[field] = Array.isArray(form[field]) ? form[field] : [];
      return acc;
    }, {}),
  };
}
